"use client";

import { useMemo, useState } from "react";
import { ShowGrid } from "@/components/features/show/discovery/show-grid";
import { ShowVenueFilter } from "@/components/features/show/filters/show-venue-filter";
import { ShowGenreFilter } from "@/components/features/show/filters/show-genre-filter";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import { SocialFilter } from "@/components/shared/filters/social/social-filter";
import { SearchEmptyState } from "@/components/features/show/states";
import { ShowDiscoverySkeleton } from "@/components/features/show/skeletons";
import { useSocialFilter } from "@/hooks/features/social";
import { useSearchInput } from "@/hooks/shared";
import { trpc } from "@/lib/trpc/react";

interface EventGroupShowsListProps {
  groupId: string;
  groupName: string;
  festivalSlug: string;
  festivalId: string;
  eventType: "main_festival" | "pre_party" | "after_party";
}

export function EventGroupShowsList({ groupId, groupName, festivalSlug, festivalId, eventType }: EventGroupShowsListProps) {
  // Filter state
  const [selectedVenue, setSelectedVenue] = useState<string>("all");
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

  // Hooks for filters
  const search = useSearchInput();
  const { isActive: selectedSocialFilter, toggle: toggleSocialFilter } = useSocialFilter();

  // Fetch shows for this event type using festival_events junction table
  // For show_list event types, query shows by event_type + event_source='show'
  const { data: shows = [], isLoading } = trpc.festival.getEventShows.useQuery(
    {
      festivalId,
      eventType,
    },
    { enabled: !!festivalId }
  );

  // Extract filter options from the actual shows data
  const venues = useMemo(() => {
    if (!shows || shows.length === 0) return [];

    const venueMap = new Map();
    shows.forEach((show) => {
      if (show.venue && show.venue_id) {
        const existing = venueMap.get(show.venue_id);
        venueMap.set(show.venue_id, {
          id: show.venue_id,
          slug: show.venue.slug || '',
          name: show.venue.name || '',
          city: show.venue.city || '',
          state: show.venue.state || '',
          show_count: (existing?.show_count || 0) + 1,
        });
      }
    });

    return Array.from(venueMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [shows]);

  const genres = useMemo(() => {
    if (!shows || shows.length === 0) return [];

    const genreSet = new Set<string>();
    shows.forEach((show) => {
      const showGenres = (show as any).genres || [];
      showGenres.forEach((genre: string) => genreSet.add(genre));
    });

    return Array.from(genreSet).sort();
  }, [shows]);

  // Client-side filtering of shows
  const filteredShows = useMemo(() => {
    let filtered = [...shows];

    // Venue filter
    if (selectedVenue && selectedVenue !== "all") {
      filtered = filtered.filter(show => show.venue_id === selectedVenue);
    }

    // Genre filter
    // Note: Shows don't currently have genre data, but filter logic is ready
    if (selectedGenres.length > 0) {
      filtered = filtered.filter(show => {
        const showGenres = (show as any).genres || [];
        return selectedGenres.some(genre => showGenres.includes(genre));
      });
    }

    // Search filter
    if (search.debouncedValue) {
      const searchLower = search.debouncedValue.toLowerCase();
      filtered = filtered.filter(show =>
        show.title?.toLowerCase().includes(searchLower) ||
        show.artist_name?.toLowerCase().includes(searchLower) ||
        show.venue?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Social filter
    // Note: Social interactions not yet implemented for shows
    if (selectedSocialFilter) {
      filtered = filtered.filter(show => {
        const interactions = (show as any).socialInteractions || {};
        // When social filter is on, show items with friends or following going
        return interactions.friendsGoing?.length > 0 || interactions.followingGoing?.length > 0;
      });
    }

    return filtered;
  }, [shows, selectedVenue, selectedGenres, search.debouncedValue, selectedSocialFilter]);

  // Check if any filters are active
  const hasActiveFilters =
    (selectedVenue !== "all" && selectedVenue !== "") ||
    selectedGenres.length > 0 ||
    search.debouncedValue !== "" ||
    selectedSocialFilter;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4 pb-6">
        {/* Filters - 2 Row Layout */}
        <div className="space-y-3">
          {/* Row 1: Venue and Genre filters */}
          <div className="grid grid-cols-2 gap-2">
            <ShowVenueFilter
              value={selectedVenue}
              onChange={setSelectedVenue}
              availableVenues={venues || []}
            />
            <ShowGenreFilter
              value={selectedGenres}
              onChange={setSelectedGenres}
              availableGenres={genres || []}
            />
          </div>

          {/* Row 2: Search and Social filter */}
          <div className="flex gap-2">
            <div className="flex-1">
              <EnhancedSearchInput
                value={search.value}
                onValueChange={search.setValue}
                placeholder="Search shows..."
                isLoading={search.isSearching}
              />
            </div>
            <SocialFilter isActive={selectedSocialFilter} onClick={toggleSocialFilter} />
          </div>
        </div>

        {/* Show skeletons */}
        <div className="space-y-3">
          <ShowDiscoverySkeleton />
          <ShowDiscoverySkeleton />
          <ShowDiscoverySkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Filters - 2 Row Layout */}
      <div className="space-y-3">
        {/* Row 1: Venue and Genre filters */}
        <div className="grid grid-cols-2 gap-2">
          <ShowVenueFilter
            value={selectedVenue}
            onChange={setSelectedVenue}
            availableVenues={venues || []}
          />
          <ShowGenreFilter
            value={selectedGenres}
            onChange={setSelectedGenres}
            availableGenres={genres || []}
          />
        </div>

        {/* Row 2: Search and Social filter */}
        <div className="flex gap-2">
          <div className="flex-1">
            <EnhancedSearchInput
              value={search.value}
              onValueChange={search.setValue}
              placeholder="Search shows..."
              isLoading={search.isSearching}
            />
          </div>
          <SocialFilter isActive={selectedSocialFilter} onClick={toggleSocialFilter} />
        </div>
      </div>

      {/* Show Results */}
      {filteredShows.length > 0 ? (
        <ShowGrid shows={filteredShows as any} showDividers={true} />
      ) : hasActiveFilters ? (
        <SearchEmptyState
          searchTerm={search.debouncedValue}
          onClearSearch={() => {
            search.handleClear();
            setSelectedVenue("all");
            setSelectedGenres([]);
          }}
        />
      ) : (
        <div className="rounded-lg border border-dashed py-12 text-center">
          <p className="text-muted-foreground">
            No related shows for this event group yet.
          </p>
        </div>
      )}
    </div>
  );
}
